import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { Heading } from "@digipost/dds-heading-react";
import Translated from "../infrastructure/translation/Translated";
import { VirksomhetsKort } from "../VirksomhetsKort/ViksomhetsKort";
import styles from "./VelgVirksomhet.module.less";
import { ManuellRegistreringKnapp } from "../ManuellRegistreringKnapp/ManuellRegistreringKnapp";
import { ManuellFinnOrgnr } from "../ManuellFindOrgnr/ManuellFindOrgnr";
import { usePreselectedOrgnr } from "../PartnerOnboarding/usePreselectedOrgnr";
import { CenteredSpinner } from "../CenteredSpinner/CenteredSpinner";
import { useRegistreringContext } from "../Registreringsflyt/useRegistreringContext";

const VelgVirksomhet: FC = (): React.ReactElement => {
  const [manualSearchToggle, setManualSearchToggle] = useState<boolean>(false);
  const hasAutoSelected = useRef(false);

  const preselectedOrgnr = usePreselectedOrgnr();
  const backToList = () => {
    setManualSearchToggle(false);
  };

  const { prokuraForetak, velgForetak, velgerForetak } =
    useRegistreringContext();

  const filtrerteVirksomheter = useMemo(() => {
    if (!preselectedOrgnr) {
      return prokuraForetak;
    }
    return prokuraForetak.filter(
      (virksomhet) => virksomhet.foretak.orgnummer === preselectedOrgnr,
    );
  }, [prokuraForetak, preselectedOrgnr]);

  const shouldAutoSelect =
    !!preselectedOrgnr && filtrerteVirksomheter.length === 1;

  useEffect(() => {
    if (shouldAutoSelect && !hasAutoSelected.current) {
      hasAutoSelected.current = true;
      velgForetak(filtrerteVirksomheter[0]);
    }
  }, [shouldAutoSelect, filtrerteVirksomheter, velgForetak]);

  const displayManualSearch = useMemo(
    () => filtrerteVirksomheter.length === 0 || manualSearchToggle,
    [filtrerteVirksomheter, manualSearchToggle],
  );

  if (shouldAutoSelect || velgerForetak) {
    return <CenteredSpinner />;
  }

  if (displayManualSearch) {
    return (
      <ManuellFinnOrgnr
        velgForetak={velgForetak}
        goBack={backToList}
        showBecauseNoResults={filtrerteVirksomheter.length === 0}
      />
    );
  }

  return (
    <section>
      <Heading level={1} size={"large"}>
        <Translated textKey={"virksomhet.velg.header"} />
      </Heading>
      <div className={styles.virksomhetsListe}>
        {filtrerteVirksomheter.map((foretakMedLenker) => (
          <VirksomhetsKort
            navn={foretakMedLenker.foretak.navn}
            orgNr={foretakMedLenker.foretak.orgnummer}
            onClickHandler={() => velgForetak(foretakMedLenker)}
            key={foretakMedLenker.foretak.orgnummer}
          />
        ))}
      </div>
      <ManuellRegistreringKnapp setShowManualSearch={setManualSearchToggle} />
    </section>
  );
};

export default VelgVirksomhet;
